/** When the chat is awake: every hour of the week, brighter where the chat talked more. */

import { Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { hourLabel } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "deep", x: 980, y: 1760, r: 330, seed: 38.4, wob: 0.16 },
  { colour: "fuchsia", x: 60, y: 280, r: 110, seed: 39.2, wob: 0.24 },
];

const DAYS = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];
const LEFT = 150;
const TOP = 430;
const COL = 36.25; // 870 wide across 24 hours
const ROW = 92;
const CELL_W = 31;
const CELL_H = 82;

/** The busiest and the quietest hour of the week. */
export function peaks(heat: number[][]) {
  let busy = { day: 0, hour: 0, n: -1 };
  let quiet = { day: 0, hour: 0, n: Infinity };
  heat.forEach((hours, day) =>
    hours.forEach((n, hour) => {
      if (n > busy.n) busy = { day, hour, n };
      if (n < quiet.n) quiet = { day, hour, n };
    }),
  );
  return { busy, quiet };
}

const when = ({ day, hour }: { day: number; hour: number }) => `${DAYS[day]} at ${hourLabel(hour)}`;

export function ChatClock({ heat }: { heat: number[][] }) {
  const { busy, quiet } = peaks(heat);
  const most = Math.max(busy.n, 1);
  // A gentle curve, so a quiet hour still shows a glow and the peak doesn't wash the rest out.
  const glow = (n: number) => (n ? 0.1 + 0.9 * (n / most) ** 0.7 : 0.05);

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.rise("[data-part=lead]", 0.45, { y: 30, sound: false });
    s.rise("[data-part=days] > *", 0.55, { x: -30, y: 0, stagger: 0.05, sound: false });
    // The week fills in hour by hour, left to right, like the day going by.
    s.tl.fromTo(
      ".heat-cell",
      { scale: 0.2, autoAlpha: 0 },
      { scale: 1, autoAlpha: 1, duration: 0.3, ease: "back.out(2)", stagger: { each: 1.4 / 24, grid: [7, 24], axis: "x", from: "start" } },
      0.7,
    );
    s.ticksAlong(0.7, 1.4, "none", 12);
    s.tl.to(".heat-cell.peak", { scale: 1.35, duration: 0.25, yoyo: true, repeat: 1, ease: "power1.out" }, 2.3);
    s.sound("sparkle", 2.3);
    s.rise("[data-part=busy]", 2.5, { y: 24 });
    s.rise("[data-part=quiet]", 2.8, { y: 24, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick" style={{ color: "var(--wr-green)" }}>
            When the chat is awake
          </div>
          <div data-part="lead" className="lead">
            Every hour of the week, all year
          </div>
        </Zone>
        <Zone at="middle">
          <div data-part="days">
            {DAYS.map((day, d) => (
              <div key={day} className="abs mono" style={{ left: 72, top: TOP + d * ROW + 24, fontSize: 30, fontWeight: 700, color: "var(--wr-muted)" }}>
                {day[0]}
              </div>
            ))}
          </div>
          {heat.map((hours, d) =>
            hours.map((n, h) => (
              <div
                key={`${d}-${h}`}
                data-testid="heat"
                className={`heat-cell${d === busy.day && h === busy.hour ? " peak" : ""}`}
                style={{ left: LEFT + h * COL, top: TOP + d * ROW, width: CELL_W, height: CELL_H, opacity: glow(n) }}
              />
            )),
          )}
          {[0, 6, 12, 18].map((h) => (
            <div key={h} className="abs mono" style={{ left: LEFT + h * COL, top: TOP + 7 * ROW + 6, fontSize: 26, fontWeight: 700, color: "var(--wr-muted)" }}>
              {hourLabel(h)}
            </div>
          ))}
        </Zone>
        <Zone at="bottom">
          <div data-part="busy" className="line" style={{ top: 1400 }}>
            The chat peaks on <b style={{ color: "var(--wr-yellow)" }}>{when(busy)}</b>.
          </div>
          <div data-part="quiet" className="line" style={{ top: 1560, color: "var(--wr-muted)" }}>
            Quietest: {when(quiet)}.
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
